import { lazy, Suspense } from "react";
import { Navigate, Route, Routes, useLocation, useMatch } from "react-router-dom";
import { useAuth } from "./auth/AuthProvider";
import { AppShell } from "./components/AppShell";
import { AuthConfirmPage } from "./pages/AuthConfirmPage";
import { LoginPage } from "./pages/LoginPage";
import { ForgotPasswordPage, ResetPasswordPage } from "./pages/PasswordResetPage";
import "./field-efficiency.css";

const DashboardPage = lazy(() =>
  import("./pages/DashboardPage").then((module) => ({ default: module.DashboardPage })),
);
const VendorsPage = lazy(() =>
  import("./pages/VendorsPage").then((module) => ({ default: module.VendorsPage })),
);
const VendorDetailPage = lazy(() =>
  import("./pages/VendorDetailPage").then((module) => ({ default: module.VendorDetailPage })),
);
const OrderDetailPage = lazy(() =>
  import("./pages/OrderDetailPage").then((module) => ({ default: module.OrderDetailPage })),
);
const FollowUpDetailPage = lazy(() =>
  import("./pages/FollowUpDetailPage").then((module) => ({
    default: module.FollowUpDetailPage,
  })),
);
const NewVendorPage = lazy(() =>
  import("./pages/NewVendorPage").then((module) => ({ default: module.NewVendorPage })),
);
const CapturePage = lazy(() =>
  import("./pages/CapturePage").then((module) => ({ default: module.CapturePage })),
);
const SettingsPage = lazy(() =>
  import("./pages/SettingsPage").then((module) => ({ default: module.SettingsPage })),
);
const ChangelogPage = lazy(() =>
  import("./pages/ChangelogPage").then((module) => ({ default: module.ChangelogPage })),
);
const AuditLogPage = lazy(() =>
  import("./pages/AuditLogPage").then((module) => ({ default: module.AuditLogPage })),
);
const FieldPlanPage = lazy(() =>
  import("./pages/FieldPlanPage").then((module) => ({ default: module.FieldPlanPage })),
);
const PreCallBriefPage = lazy(() =>
  import("./pages/PreCallBriefPage").then((module) => ({ default: module.PreCallBriefPage })),
);

export function App() {
  const { user, loading, configurationError } = useAuth();

  const location = useLocation();
  const confirmationRoute = useMatch("/auth/confirm");

  const forgotPasswordRoute = useMatch("/auth/forgot-password");
  const resetPasswordRoute = useMatch("/auth/reset-password");

  if (forgotPasswordRoute) return <ForgotPasswordPage />;
  if (resetPasswordRoute) return <ResetPasswordPage />;

  if (confirmationRoute) return <AuthConfirmPage key={location.search} />;

  if (loading) {
    return <div className="full-page-message">Opening your CRM...</div>;
  }

  if (configurationError) {
    return (
      <div className="full-page-message full-page-message--card">
        <h1>CRM setup is incomplete</h1>
        <p>Add the Supabase URL and public key to the deployment environment.</p>
      </div>
    );
  }

  if (!user) return <LoginPage />;

  return (
    <AppShell>
      <Suspense fallback={<div className="full-page-message">Loading...</div>}>
        <Routes>
          <Route path="/" element={<DashboardPage />} />
          <Route path="/accounts" element={<VendorsPage />} />
          <Route path="/accounts/new" element={<NewVendorPage />} />
          <Route path="/accounts/:vendorId/brief" element={<PreCallBriefPage />} />
          <Route path="/accounts/:vendorId" element={<VendorDetailPage />} />
          <Route path="/vendors" element={<VendorsPage />} />
          <Route path="/vendors/new" element={<NewVendorPage />} />
          <Route path="/vendors/:vendorId/brief" element={<PreCallBriefPage />} />
          <Route path="/vendors/:vendorId" element={<VendorDetailPage />} />
          <Route path="/orders/:orderId" element={<OrderDetailPage />} />
          <Route path="/follow-ups/:followUpId" element={<FollowUpDetailPage />} />
          <Route path="/capture" element={<CapturePage />} />
          <Route path="/field" element={<FieldPlanPage />} />
          <Route path="/nearby" element={<Navigate to="/field" replace />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/settings/changelog" element={<ChangelogPage />} />
          <Route path="/settings/audit-log" element={<AuditLogPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </AppShell>
  );
}
